import AuthorConvertButton from '../../components/crm/AuthorConvertButton'
import { stripJatsXml } from '../../utils/stripJatsXml'
import { useState, useRef, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  BookOpen, ChevronRight, ExternalLink, Search, ArrowLeft, SortAsc,
  Bookmark, Check, AlertCircle, Filter, CalendarRange, Link2, X,
} from 'lucide-react'
import { getJournalTopics, browseJournals, searchJournals, getPersonalLists, addListItem, getProjects } from '../../services/api'
import type { JournalArticle, TopicFacet } from '../../services/api'
import { topicFacetComparator } from '../../utils/topicSort'
import { formatTopicLabel } from '../../utils/topics'
import { useTimedOut } from '../../hooks/useTimedOut'
import type { PersonalList, Project } from '../../types'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import AttachToProjectMenu from '../../components/journals/AttachToProjectMenu'
import EmptyState from '../../components/ui/EmptyState'
import PageHeader from '../../components/ui/PageHeader'
import ContextualTip from '../../components/ui/ContextualTip'

function CardAddToList({ journalId, journalTitle }: { journalId: string; journalTitle: string }) {
  const [open, setOpen] = useState(false)
  const [added, setAdded] = useState<string | null>(null)
  const [addError, setAddError] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  const { data: lists } = useQuery({
    queryKey: ['personal-lists'],
    queryFn: getPersonalLists,
    enabled: open,
  })

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleAdd = async (list: PersonalList) => {
    setAddError(null)
    try {
      await addListItem(list.list_id, {
        resource_id: journalId,
        resource_type: 'journal',
        title: journalTitle,
      })
      setAdded(list.list_id)
      queryClient.invalidateQueries({ queryKey: ['list-items', list.list_id] })
      setTimeout(() => { setAdded(null); setOpen(false) }, 1200)
    } catch (err) {
      // BUG-127: only a 409 means "already in this list" — a benign duplicate we
      // can treat as success. Every other outcome (403/500/network) is a real
      // failure and must be surfaced instead of silently swallowed.
      const e = err as { response?: { status?: number; data?: { error?: string } }; message?: string }
      if (e?.response?.status === 409) {
        setAdded(list.list_id)
        setTimeout(() => { setAdded(null); setOpen(false) }, 1200)
      } else {
        setAddError(e?.response?.data?.error || e?.message || 'Failed to add to list')
      }
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
        className="p-1.5 text-gray-500 hover:text-iflow-600 flex-shrink-0"
        title="Add to list"
      >
        <Bookmark size={14} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-48 light-card shadow-lg z-20 py-1">
          {!lists || lists.length === 0 ? (
            <p className="px-3 py-2 text-xs text-gray-500">No lists yet.</p>
          ) : (
            lists.map((l) => (
              <button
                key={l.list_id}
                onClick={(e) => { e.stopPropagation(); handleAdd(l) }}
                className="w-full flex items-center justify-between px-3 py-1.5 text-xs text-gray-700 hover:bg-white/50"
              >
                <span className="truncate">{l.name}</span>
                {added === l.list_id && <Check size={12} className="text-green-500 flex-shrink-0" />}
              </button>
            ))
          )}
          {addError && (
            <p data-testid="add-list-error" className="px-3 py-2 text-xs text-red-600 border-t border-gray-100">{addError}</p>
          )}
        </div>
      )}
    </div>
  )
}

function JournalCard({ journal, projects }: { journal: JournalArticle; projects?: Project[] }) {
  const navigate = useNavigate()

  return (
    <div
      onClick={() => navigate(`/journals/${encodeURIComponent(journal._id)}`)}
      className="light-card p-4 hover:border-iflow-300 transition-colors cursor-pointer"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">{journal.title}</h3>
          {journal.authors && journal.authors.length > 0 && (
            <div className="text-xs text-gray-500 mt-1 flex items-center gap-1 flex-wrap">
              {journal.authors.slice(0, 3).map((author: string, idx: number) => (
                <span key={idx} className="inline-flex items-center">
                  {idx > 0 && ', '}{author}
                  <AuthorConvertButton authorString={author} sourceDocumentId={journal._id}
                    sourceDocumentTitle={journal.title} sourceType="journal" />
                </span>
              ))}
              {journal.authors.length > 3 && <span> +{journal.authors.length - 3} more</span>}
            </div>
          )}
          {journal.abstract && (
            <p className="text-xs text-gray-500 mt-2 line-clamp-3">{stripJatsXml(journal.abstract)}</p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <CardAddToList journalId={journal._id} journalTitle={journal.title} />
          <AttachToProjectMenu projects={projects} journalId={journal._id} journalTitle={journal.title} />
          {journal.doi && (
            <a
              href={`https://doi.org/${journal.doi}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-1.5 text-gray-500 hover:text-iflow-600"
              title="Open DOI"
            >
              <ExternalLink size={14} />
            </a>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        {journal.peer_reviewed && (
          <span className="px-2 py-0.5 text-[10px] font-medium bg-green-500/20 text-green-700 rounded-full">Peer Reviewed</span>
        )}
        {journal.open_access && (
          <span className="px-2 py-0.5 text-[10px] font-medium bg-iflow-50 text-iflow-700 rounded-full">Open Access</span>
        )}
        {journal.citation_count != null && journal.citation_count > 0 && (
          <span className="text-[10px] text-gray-500">{journal.citation_count} citations</span>
        )}
        {journal.journal_name && (
          <span className="text-[10px] text-gray-500 truncate">{journal.journal_name}</span>
        )}
      </div>
      {journal.topics && journal.topics.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {journal.topics.slice(0, 5).map((t) => (
            <span key={t} className="px-1.5 py-0.5 bg-sky-500/10 text-gray-600 text-[10px] rounded">
              {formatTopicLabel(t)}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function TopicSidebar({
  topics,
  selectedTopic,
  onSelect,
  breadcrumb,
  onBack,
  isLoading,
  timedOut,
  isError,
  onRetry,
}: {
  topics: TopicFacet[]
  selectedTopic: string
  onSelect: (topic: string) => void
  breadcrumb: string[]
  onBack: () => void
  isLoading: boolean
  /** BUG-VIS-4-3: true once `isLoading` has stayed true past a bounded wait —
   * lets the sidebar fall back to an honest retry state instead of an
   * infinite skeleton. */
  timedOut?: boolean
  isError?: boolean
  onRetry?: () => void
}) {
  // Never spin forever: once we've timed out (or the query genuinely
  // errored) and still have nothing to show, surface a real retry state
  // instead of leaving the skeleton up indefinitely.
  const showStalled = (timedOut || isError) && topics.length === 0
  return (
    <div className="light-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen size={14} className="text-iflow-500" />
        <h3 className="text-sm font-semibold text-gray-700">Subject Areas</h3>
      </div>
      {breadcrumb.length > 0 && (
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-xs text-iflow-600 hover:text-iflow-700 mb-2 font-medium"
        >
          <ArrowLeft size={12} /> Back
        </button>
      )}
      {breadcrumb.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-[10px] text-gray-500 mb-2">
          {breadcrumb.map((b, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 && <ChevronRight size={8} />}
              <span className="truncate max-w-[100px]">{b}</span>
            </span>
          ))}
        </div>
      )}
      {isLoading && !showStalled ? (
        <div className="space-y-2" data-testid="journal-topics-skeleton">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-8 bg-sky-500/10 rounded animate-pulse" />
          ))}
        </div>
      ) : showStalled ? (
        <div className="text-center py-4" data-testid="journal-topics-stalled">
          <p className="text-xs text-gray-500 mb-2">
            {isError ? 'Subject areas failed to load.' : 'Subject areas are taking longer than expected.'}
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="text-xs font-medium text-iflow-600 hover:text-iflow-700 underline"
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="space-y-0.5 max-h-[60vh] overflow-y-auto">
          {[...topics].sort(topicFacetComparator).map((t) => (
            <button
              key={t.topic}
              onClick={() => onSelect(t.topic)}
              className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded-md transition-colors ${
                selectedTopic === t.topic
                  ? 'bg-iflow-50 text-iflow-700 font-medium'
                  : 'text-gray-600 hover:bg-sky-500/10'
              }`}
            >
              <span className="truncate">{t.topic}</span>
              <span className="text-[10px] text-gray-500 ml-1 flex-shrink-0">
                {(t.count ?? 0).toLocaleString()}
              </span>
            </button>
          ))}
          {topics.length === 0 && !isLoading && (
            <p className="text-xs text-gray-500 px-2 py-2">No topics found</p>
          )}
        </div>
      )}
    </div>
  )
}

export default function JournalBrowse() {
  const navigate = useNavigate()
  const [categoriesOpen, setCategoriesOpen] = useState(false)
  useEffect(() => {
    if (!categoriesOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setCategoriesOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [categoriesOpen])
  const [selectedTopic, setSelectedTopic] = useState('')
  const [breadcrumb, setBreadcrumb] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  // BUG-155: the search runs against a submitted SNAPSHOT, not the live input,
  // so typing after a search (or the input reflecting a value) doesn't refire
  // the query on every keystroke — mirrors the Patents `submittedQuery` fix.
  const [submittedQuery, setSubmittedQuery] = useState('')
  const [searchSubmitted, setSearchSubmitted] = useState(false)
  const [sort, setSort] = useState('relevance')
  const [page, setPage] = useState(0)
  const [selectedProjectId, setSelectedProjectId] = useState('')

  // Source / Has-DOI / Year facets. None of these are accepted as query
  // params by browseJournals or searchJournals, so they're applied
  // client-side to the currently loaded page of real results — same
  // trade-off as the Patents facets (narrows this page, not the whole index).
  const [peerReviewedOnly, setPeerReviewedOnly] = useState(false)
  const [openAccessOnly, setOpenAccessOnly] = useState(false)
  const [hasDoiOnly, setHasDoiOnly] = useState(false)
  const [yearFrom, setYearFrom] = useState('')
  const [yearTo, setYearTo] = useState('')

  const clearFacetFilters = () => {
    setPeerReviewedOnly(false)
    setOpenAccessOnly(false)
    setHasDoiOnly(false)
    setYearFrom('')
    setYearTo('')
  }

  // Load user's projects for domain-boosted topic ranking
  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: getProjects,
  })

  // Load topics (optionally filtered by parent topic; domain-boosted when project selected)
  const parentTopic = breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1] : undefined
  const {
    data: topicData,
    isLoading: topicsLoading,
    isError: topicsError,
    refetch: refetchTopics,
  } = useQuery({
    queryKey: ['journal-topics', parentTopic, selectedProjectId],
    queryFn: () => getJournalTopics({ size: 50, parent: parentTopic, project_id: selectedProjectId || undefined }),
  })
  // BUG-VIS-4-3: bound the sidebar's loading state — the OpenSearch topic
  // aggregation is normally fast, but if it ever genuinely stalls, don't
  // leave the skeleton (or the header count) spinning forever.
  const topicsTimedOut = useTimedOut(topicsLoading, 8000)

  // Browse journals when a topic is selected
  const { data: browseData, isLoading: browseLoading, isError: browseError } = useQuery({
    queryKey: ['journal-browse', selectedTopic, sort, page],
    queryFn: () => browseJournals({ topic: selectedTopic, size: 20, from: page * 20, sort }),
    enabled: !!selectedTopic && !searchSubmitted,
  })

  // Search journals. BUG-144: the search backend (`search_journals`) returns a
  // single relevance-ranked page — it has no `from`/offset and no server-side
  // sort. So fetch a larger page and paginate it client-side (see the slice
  // below), and hide the sort dropdown in search mode. The old code showed a
  // pager + sort that were pure no-ops (neither `page`/`from` nor `sort` was in
  // the call or the query key).
  const { data: searchData, isLoading: searchLoading, isError: searchError } = useQuery({
    queryKey: ['journal-search', submittedQuery, selectedTopic],
    queryFn: () => searchJournals({ query: submittedQuery, size: 100, topic: selectedTopic || undefined }),
    enabled: searchSubmitted && !!submittedQuery,
  })

  const handleTopicSelect = (topic: string) => {
    setCategoriesOpen(false)
    setSelectedTopic(topic)
    setPage(0)
    setSearchSubmitted(false)
    clearFacetFilters()
  }

  const handleBack = () => {
    setBreadcrumb((prev) => prev.slice(0, -1))
    setSelectedTopic('')
    setPage(0)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const q = searchQuery.trim()
    if (q) {
      setSubmittedQuery(q)
      setSearchSubmitted(true)
      setPage(0)
      clearFacetFilters()
    }
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    setSubmittedQuery('')
    setSearchSubmitted(false)
    clearFacetFilters()
  }

  // BUG-144: browse is paginated server-side (from=page*20); search returns a
  // single fetched page, so paginate it client-side and count from the fetched
  // set (mirrors the Patents page).
  const allSearchJournals = searchData?.journals || []
  const journals = searchSubmitted
    ? allSearchJournals.slice(page * 20, page * 20 + 20)
    : browseData?.journals
  const totalJournals = searchSubmitted ? allSearchJournals.length : browseData?.total
  const isLoading = searchSubmitted ? searchLoading : browseLoading

  // Source / Has-DOI / Year facets — client-side over the currently loaded page.
  const visibleJournals = useMemo(() => {
    const list = journals || []
    return list.filter((j) => {
      if (peerReviewedOnly && !j.peer_reviewed) return false
      if (openAccessOnly && !j.open_access) return false
      if (hasDoiOnly && !j.doi) return false
      const year = j.publication_date ? j.publication_date.slice(0, 4) : ''
      if (yearFrom && (!year || year < yearFrom)) return false
      if (yearTo && (!year || year > yearTo)) return false
      return true
    })
  }, [journals, peerReviewedOnly, openAccessOnly, hasDoiOnly, yearFrom, yearTo])

  const facetFiltersActive = peerReviewedOnly || openAccessOnly || hasDoiOnly || !!yearFrom || !!yearTo

  // BUG-VIS-4-3: the count is real, live data — it must never render as a
  // bare "—" while it's simply still in flight. Show an honest "Loading…"
  // until we have a number, and only fall back to an "unavailable" label
  // once the query has genuinely timed out or errored.
  const articlesIndexedLabel = (() => {
    const total = topicData?.total_journals
    if (total != null) return `${total.toLocaleString()} articles indexed`
    if (topicsError || topicsTimedOut) return 'Article count unavailable'
    return 'Loading articles…'
  })()

  // UX-030: below lg the topic list + facets live in a drawer; the same fragment
  // renders in the desktop column so the two can never drift apart.
  const sidebarContent = (
    <>
          <TopicSidebar
            topics={topicData?.topics || []}
            selectedTopic={selectedTopic}
            onSelect={handleTopicSelect}
            breadcrumb={breadcrumb}
            onBack={handleBack}
            isLoading={topicsLoading}
            timedOut={topicsTimedOut}
            isError={topicsError}
            onRetry={() => refetchTopics()}
          />
          {/* Sub-topics when browsing */}
          {browseData?.sub_topics && browseData.sub_topics.length > 0 && selectedTopic && (
            <div className="mt-4 light-card p-4">
              <h4 className="text-xs font-semibold text-gray-700 mb-2">Related Topics</h4>
              <div className="space-y-0.5 max-h-48 overflow-y-auto">
                {[...browseData.sub_topics].sort(topicFacetComparator).slice(0, 15).map((t) => (
                  <button
                    key={t.topic}
                    onClick={() => {
                      setBreadcrumb((prev) => [...prev, selectedTopic])
                      setCategoriesOpen(false)
                      setSelectedTopic(t.topic)
                      setPage(0)
                    }}
                    className="w-full flex items-center justify-between px-2 py-1 text-xs text-gray-600 hover:bg-sky-500/10 rounded"
                  >
                    <span className="truncate">{t.topic}</span>
                    <span className="text-[10px] text-gray-500 ml-1">{(t.count ?? 0).toLocaleString()}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Source / Has-DOI facet — client-side over the loaded page (neither
              browseJournals nor searchJournals accept these as filters). */}
          {(selectedTopic || searchSubmitted) && (
            <div className="mt-4 light-card p-4">
              <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                <Filter size={12} /> Source
              </h4>
              <div className="space-y-1.5">
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={peerReviewedOnly}
                    onChange={(e) => setPeerReviewedOnly(e.target.checked)}
                    className="rounded text-iflow-600"
                  />
                  Peer-reviewed only
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={openAccessOnly}
                    onChange={(e) => setOpenAccessOnly(e.target.checked)}
                    className="rounded text-iflow-600"
                  />
                  Open access only
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasDoiOnly}
                    onChange={(e) => setHasDoiOnly(e.target.checked)}
                    className="rounded text-iflow-600"
                  />
                  <Link2 size={11} className="text-gray-500" /> Has a DOI
                </label>
              </div>
            </div>
          )}
          {/* Year facet — client-side, same reasoning as Source above. */}
          {(selectedTopic || searchSubmitted) && (
            <div className="mt-4 light-card p-4">
              <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                <CalendarRange size={12} /> Year
              </h4>
              <p className="text-[10px] text-gray-500 mb-2">Publication year — narrows this page's results.</p>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder="From"
                  value={yearFrom}
                  onChange={(e) => setYearFrom(e.target.value)}
                  className="w-full px-2 py-1 text-xs light-input outline-none"
                  data-testid="journal-year-from-filter"
                  aria-label="Publication year from"
                />
                <span className="text-gray-500 text-xs">to</span>
                <input
                  type="number"
                  placeholder="To"
                  value={yearTo}
                  onChange={(e) => setYearTo(e.target.value)}
                  className="w-full px-2 py-1 text-xs light-input outline-none"
                  data-testid="journal-year-to-filter"
                  aria-label="Publication year to"
                />
              </div>
            </div>
          )}
    </>
  )

  return (
    <div>
      <PageHeader
        title="Journal Articles"
        description="Search open-access research, then save papers to a list or link them to a project to build your evidence base."
        helpKey="journals"
        status={{ label: articlesIndexedLabel, tone: 'neutral' }}
        secondaryActions={
          <button
            onClick={() => navigate('/lists')}
            className="px-3 py-2 text-sm font-medium text-iflow-700 bg-white border border-iflow-200 rounded-lg hover:bg-iflow-50 flex items-center gap-1.5"
          >
            <Bookmark size={14} /> Saved Lists
          </button>
        }
      />

      <div className="mb-6">
        <ContextualTip id="journals-intro">
          Bookmark any paper to add it to a list, or link it to a project to build its evidence base. Pick a project below to rank subject areas by that project&rsquo;s domain.
        </ContextualTip>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); if (!e.target.value) setSearchSubmitted(false) }}
              placeholder="Search journal articles..."
              className="w-full pl-9 pr-3 py-2 light-input text-sm focus:ring-2 focus:ring-iflow-500 focus:border-iflow-500 outline-none"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800 transition-colors"
          >
            Search
          </button>
          {searchSubmitted && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="px-3 py-2 text-sm text-gray-600 border-sky-200 hover:bg-white/50 border rounded-lg"
            >
              Clear
            </button>
          )}
        </div>
      </form>

      {(topicsError || browseError || searchError) && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-red-500/10 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle size={16} className="flex-shrink-0" />
          <span>Failed to load {topicsError ? 'subject topics' : searchError ? 'search results' : 'articles'}. The data index may be temporarily unavailable.</span>
        </div>
      )}

      {/* Project domain filter — boosts topics matching the selected project's domain */}
      {projects && projects.length > 0 && (
        <div className="flex items-center gap-2 mb-4 text-sm">
          <span className="text-gray-500 text-xs">Filter by project:</span>
          <select
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            className="px-2 py-1 text-xs light-input rounded-lg focus:ring-1 focus:ring-iflow-500 outline-none"
            data-testid="journal-project-selector"
          >
            <option value="">— all domains —</option>
            {projects.map((p) => (
              <option key={p.project_id} value={p.project_id}>{p.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* UX-030: the topic list is a sidebar only from lg up; on phones and tablets
          it opens from this button instead. */}
      <div className="lg:hidden mb-4">
        <button
          type="button"
          onClick={() => setCategoriesOpen(true)}
          aria-haspopup="dialog"
          className="flex items-center gap-2 text-sm font-medium text-gray-700 px-4 py-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50"
        >
          <Filter size={16} />
          Categories / Filters
          {selectedTopic && (
            <span className="max-w-[10rem] truncate rounded-full bg-iflow-50 px-2 py-0.5 text-xs text-iflow-700">{selectedTopic}</span>
          )}
        </button>
      </div>
      {categoriesOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Categories and filters">
          <div className="absolute inset-0 bg-black/30" onClick={() => setCategoriesOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-80 max-w-[90vw] bg-white shadow-xl p-5 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900">Categories / Filters</h3>
              <button type="button" onClick={() => setCategoriesOpen(false)} aria-label="Close categories and filters" className="text-gray-500 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            {sidebarContent}
          </div>
        </div>
      )}

      <div className="flex gap-6">
        {/* Topic sidebar — desktop column. Below lg the same content opens in the
            Categories / Filters drawer (UX-030). */}
        <div className="hidden lg:block w-64 flex-shrink-0">{sidebarContent}</div>

        {/* Journal list */}
        <div className="flex-1 min-w-0">
          {selectedTopic || searchSubmitted ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  {selectedTopic && !searchSubmitted && (
                    <h2 className="text-lg font-semibold text-gray-900">{selectedTopic}</h2>
                  )}
                  {searchSubmitted && (
                    <h2 className="text-lg font-semibold text-gray-900">Results for "{searchQuery}"</h2>
                  )}
                  <p className="text-sm text-gray-500">
                    {totalJournals?.toLocaleString() || 0} articles
                    {facetFiltersActive && visibleJournals.length !== (journals || []).length && (
                      <> &middot; {visibleJournals.length} shown on this page</>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {facetFiltersActive && (
                    <button type="button" onClick={clearFacetFilters} className="text-xs text-iflow-600 hover:text-iflow-700 underline">
                      Clear filters
                    </button>
                  )}
                  {/* BUG-144: sort applies only to topic browsing — the search
                      backend has no server-side sort, so hide the (previously
                      no-op) control in search mode. */}
                  {!searchSubmitted && (
                    <div className="flex items-center gap-2">
                      <SortAsc size={14} className="text-gray-500" />
                      <select
                        value={sort}
                        onChange={(e) => { setSort(e.target.value); setPage(0) }}
                        className="px-2 py-1 text-xs light-input rounded-lg focus:ring-1 focus:ring-iflow-500 outline-none"
                      >
                        <option value="relevance">Relevance</option>
                        <option value="citations">Most Cited</option>
                        <option value="newest">Newest</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {isLoading ? (
                <LoadingSkeleton rows={5} type="list" />
              ) : journals && journals.length > 0 ? (
                <div className="space-y-3">
                  {visibleJournals.length === 0 ? (
                    <div className="text-sm text-gray-500 py-6 text-center border border-dashed border-gray-200 rounded-lg">
                      No articles on this page match the selected filters.{' '}
                      <button type="button" onClick={clearFacetFilters} className="text-iflow-600 underline">
                        Clear filters
                      </button>
                    </div>
                  ) : (
                    visibleJournals.map((j) => (
                      <JournalCard key={j._id} journal={j} projects={projects} />
                    ))
                  )}
                  {/* Pagination */}
                  {totalJournals != null && totalJournals > 20 && (
                    <div className="flex items-center justify-between pt-4">
                      <button
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        disabled={page === 0}
                        className="px-3 py-1.5 text-sm border border-sky-200 rounded-lg hover:bg-white/50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="text-xs text-gray-500">
                        Page {page + 1} of {Math.ceil(totalJournals / 20)}
                      </span>
                      <button
                        onClick={() => setPage((p) => p + 1)}
                        disabled={(page + 1) * 20 >= totalJournals}
                        className="px-3 py-1.5 text-sm border border-sky-200 rounded-lg hover:bg-white/50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <EmptyState
                  icon={BookOpen}
                  title="No articles found"
                  description="Try a different topic or search term."
                />
              )}
            </>
          ) : (
            <EmptyState
              icon={BookOpen}
              title="Browse journal articles"
              description="Search above, or open Categories to browse by subject area."
            />
          )}
        </div>
      </div>
    </div>
  )
}
